// #region angular imports
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, computed, inject, signal, viewChild, effect, untracked, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { 
    faBiohazard
    , faCalendarLinesPen
    , faGarage
    , faHandHoldingMedical
    , faHandshake
    , faJedi
    , faMoneyBillTransfer
    , faShelves
    , faSpinnerThird 
} from '@fortawesome/pro-solid-svg-icons';
import { NgbModal, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { Popover, PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, tap } from 'rxjs';
// #endregion

// #region app imports
import { SecurityService } from 'app/core/security/security.service';
import { InventoryTypeEnum } from 'app/core/enums/generated/InventoryTypeEnum';
import { SysInvInventoryVm } from 'app/shared/generated/SysInv/Models/SysInvInventoryVm';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { ConfirmationModalComponent } from 'app/shared/form-elements/components/confirmation-modal/confirmation-modal.component';
import { ReconTypeEnum } from 'app/core/enums/generated/ReconTypeEnum';
import { InventoryChoiceEnum } from 'app/core/enums/generated/InventoryChoiceEnum';
import { ReconciliationVm } from 'app/shared/generated/SysInv/Models/Reconciliation/ReconciliationVm';
import { IssuanceService } from 'app/formulary-inventory/issuance/issuance.service';
import { NotificationService } from 'app/notification.service';
import { OrderingBlockVm } from 'app/shared/generated/SysInv/Models/Orders/Ordering/OrderingBlockVm';
import { SysInvTransferService } from 'app/formulary-inventory/transfers/transfer.service';
import { SelectItem } from 'app/shared/models/select-item';
import { OrderingService } from 'app/formulary-inventory/ordering/ordering.service';
import { ScanProductStandaloneComponent } from 'app/shared/scan-product-standalone/scan-product-standalone.component';
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { ProductBarcode } from 'app/inventory/services/product-barcode';
import { parsePidScan, QuarantineIntakeService } from 'app/shared/quarantine-intake/quarantine-intake.service';
import { ScanPidComponent } from 'app/shared/scan-pid/scan-pid.component';
import { InventoryService } from 'app/inventory/services/inventory.service';
import { LoosePillContainerVm } from 'app/shared/generated/Quarantines/Models/LoosePills/LoosePillContainerVm';
import { SysInvLoosePillsService } from 'app/formulary-inventory/sys-inv-loose-pills/sys-inv-loose-pills.service';
import { SysInvEditLoosePillComponent } from 'app/formulary-inventory/sys-inv-loose-pills/sys-inv-loose-pill-container/sys-inv-loose-pill-list/sys-inv-edit-loose-pill/sys-inv-edit-loose-pill.component';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { SysInvWorkflowEnum } from 'app/core/enums/generated/SysInvWorkflowEnum';
import { ColorService } from 'app/shared/services/color.service';
import { ReconStatusEnum } from 'app/core/enums/generated/ReconStatusEnum';
import { FTSUStatusEnum } from 'app/core/enums/generated/FTSUStatusEnum';
// #endregion

@Component({
    selector: 'pcg-sys-inv-shortcuts'
    , imports: [
        NgTemplateOutlet
        , NgClass
        , FormsModule
        , ReactiveFormsModule
        , NgTemplateOutlet
        , NgbTypeaheadModule
        , FontAwesomeModule
        , ButtonModule
        , PopoverModule
        , SelectModule
        , TooltipModule
        , FloatLabelModule
        , InputMaskModule
        , BadgeModule
        , SkeletonModule
        , ScanProductStandaloneComponent
        , ScanPidComponent
        , SysInvEditLoosePillComponent
    ]
    , templateUrl: './sys-inv-shortcuts.component.html'
    , changeDetection: ChangeDetectionStrategy.Eager
    , styleUrls: ['./sys-inv-shortcuts.component.scss']
})
export class SysInvShortcutsComponent {

    // #region injections
    sec = inject(SecurityService);
    router = inject(Router);
    http = inject(HttpClient);
    modalService = inject(NgbModal);
    issuanceService = inject(IssuanceService);
    toastr = inject(NotificationService);
    transferService = inject(SysInvTransferService);
    orderService = inject(OrderingService);
    inventoryService = inject(InventoryService);
    loosePillService = inject(SysInvLoosePillsService);
    infoService = inject(QuickUserInfoService);
    // #endregion

    InventoryTypeEnum = InventoryTypeEnum;
    QuarantineTypeEnum = QuarantineTypeEnum;
    faSpinnerThird = faSpinnerThird;
    
    // #region Configuration
    inventories = signal<SysInvInventoryVm[]>([]);
    availableInventories = computed(() => this.inventories().filter(i => i.ftsuStatus == FTSUStatusEnum.Completed));
    popoverShortcut = signal<ShortcutItem | null>(null);
    activeAddtionalTemplate = signal<boolean>(false);
    selectedInventory = signal<SysInvInventoryVm | null>(null);

    isBphp = computed(() => { return this.inventories().some(i => i.isBPHP); });

    needsChdInventoryChoice = computed(() => {
        const inventories = this.availableInventories();
        if (this.isBphp()) { return false; }
        return inventories.length > 1;
    });

    op = viewChild<Popover>('op');
    popoverResizeObserver?: ResizeObserver;

    shortcuts = computed<ShortcutItem[]>(() => {
        let shortcuts: ShortcutItem[] = [];
        if (this.availableInventories().length > 0) {
            shortcuts = [     
                {
                    title: 'Inventory'
                    , icon: faShelves
                    , action: value => this.openInventory(value)
                    , options: this.inventoryOptions()
                    , disabled: this.inventoryDisabled()
                }
                , {
                    title: 'Order'
                    , icon: faJedi
                    , workflow: SysInvWorkflowEnum.Order
                    , action: value => this.tryCreateOrder(value)
                    , options: this.orderOptions()
                    , disabled: this.orderDisabled()
                }
                , {
                    title: 'Receive'
                    , icon: faHandHoldingMedical
                    , workflow: SysInvWorkflowEnum.Receiving
                    , action: value => this.openReceivingList(value)
                    , options: this.receivingOptions()
                    , disabled: this.receivingDisabled()
                }
                , {
                    title: 'Transfer'
                    , icon: faMoneyBillTransfer
                    , workflow: SysInvWorkflowEnum.Transfer
                    , action: value => this.openTransferOptions(value)
                    , options: this.transferOptions()
                    , disabled: this.transferDisabled()
                    , additionalTemplate: 'transfer'
                }
                , {
                    title: 'Reconciliation'
                    , icon: faHandshake
                    , workflow: SysInvWorkflowEnum.Reconciliation
                    , action: value => this.openReconciliationSelect(value)
                    , options: this.reconOptions()
                    , disabled: this.reconDisabled()
                }
                , {
                    title: 'Quarantine'
                    , icon: faBiohazard
                    , workflow: SysInvWorkflowEnum.Quarantine
                    , action: value => this.openQuarantineSelect(value)
                    , options: this.quarantineOptions()
                    , disabled: this.quarantineDisabledComputed()
                    , additionalTemplate: 'quarantine'
                }
            ];
            
            if (!this.isBphp()) {
                shortcuts.push(
                {
                    title: 'Issuance'
                    , icon: faCalendarLinesPen
                    , workflow: SysInvWorkflowEnum.Issuance
                    , action: value => this.tryCreateIssuance(value)
                    , options: this.issuanceOptions()
                    , disabled: this.issuanceDisabled()
                });
            }
        }

        if (
            this.inventories().some(i => i.ftsuStatus != FTSUStatusEnum.Completed) 
            && !this.isBphp()
        ) {
            shortcuts.push(
            {
                title: 'First Time Setup'
                , icon: faGarage
                , workflow: SysInvWorkflowEnum.FirstTimeSetup
                , action: value => this.openFtsu(value)
                , options: this.ftsuOptions()
                , disabled: this.ftsuDisabled()
            });
        }
        return shortcuts;
    });

    routingWorkflow = signal<SysInvWorkflowEnum>(null!);
    // #endregion

    // #region Inventory Config
    inventoryOptions = computed<ShortcutOption[]>(() => {
        // BPHP has one "Both" inventory, but user still chooses which tab/view.
        if (this.isBphp()) {
            return [
                { label: 'Bulk', value: InventoryChoiceEnum.Bulk }
                , { label: 'Staging', value: InventoryChoiceEnum.Staging }
                , { label: 'PID', value: InventoryChoiceEnum.PID }
            ];
        }

        // CHD with one bulk + one issuance inventory.
        if (this.needsChdInventoryChoice()) {
            return [
                { label: 'Bulk', value: InventoryChoiceEnum.Bulk }
                , { label: 'Issuance', value: InventoryChoiceEnum.PID }
            ];
        }

        // CHD with one inventory does not need a popover.
        return [];
    });

    inventoryDisabled = signal<boolean>(false);
    // #endregion

    // #region Order Config
    orderOptions = computed<ShortcutOption[]>(() => {
        if (
            this.needsChdInventoryChoice() 
            || this.isBphp()
        ) {
            return [
                { label: 'Bulk', value: InventoryTypeEnum.Bulk }
                , { label: 'Issuance', value: InventoryTypeEnum.Issuance }
            ];
        }

        return [];
    });

    orderDisabled = linkedSignal<boolean>(() => 
        this.orderService.isCreating() || this.routingWorkflow() === SysInvWorkflowEnum.Order
    );
    // #endregion

    // #region Receiving Config
    receivingOptions = computed<ShortcutOption[]>(() => {
        if (this.needsChdInventoryChoice()) {
            return [
                { label: 'Bulk', value: InventoryTypeEnum.Bulk }
                , { label: 'Issuance', value: InventoryTypeEnum.Issuance }
            ];
        }

        return [];
    });

    receivingDisabled = linkedSignal<boolean>(() => {
        if (this.routingWorkflow() === SysInvWorkflowEnum.Receiving) { return true; }
        return false;
    });
    // #endregion

    // #region Transfer Config
    transferOptions = computed<ShortcutOption[]>(() => {
        if (
            this.isBphp() 
            || this.needsChdInventoryChoice()
        ) {
            return [
                { label: 'Bulk', value: InventoryTypeEnum.Bulk }
                , { label: 'Issuance', value: InventoryTypeEnum.Issuance }
            ];
        }
        return [];
    });

    transferDisabled = linkedSignal<boolean>(() => {
        if (this.routingWorkflow() === SysInvWorkflowEnum.Transfer) { return true; }
        return false;
    });

    inventoryTransferSelectItems = signal<SelectItem[]>([]);
    transferItemsLoading = signal<boolean>(false);
    destinationTransferInventoryId = signal<number | null>(null);
    outOfCountyTransferComment = signal<string>('');
    selectedInventoryTransferType = signal<InventoryTypeEnum | null>(null);
    isOutOfCountyTransfer = signal<boolean>(false);
    // #endregion

    // #region Reconciliation Config
    reconOptions = computed<ShortcutOption[]>(() => {
        if (this.needsChdInventoryChoice()) {
            return [
                { label: 'Bulk', value: ReconTypeEnum.Bulk }
                , { label: 'PID', value: ReconTypeEnum.PID }
            ];
        }
        if (this.isBphp()) {
            return [
                { label: 'Bulk', value: ReconTypeEnum.Bulk }
                , { label: 'Staging', value: ReconTypeEnum.Staging }
                , { label: 'PID', value: ReconTypeEnum.PID }
            ];
        }

        return [];
    });

    reconDisabled = linkedSignal<boolean>(() => {
        if (this.routingWorkflow() === SysInvWorkflowEnum.Reconciliation) { return true; }
        return false;
    });
    // #endregion

    // #region Quarantine Config
    quarantineOptions = computed<ShortcutOption[]>(() => {
        if (this.needsChdInventoryChoice()) {
            return [
                { label: 'Bulk', value: QuarantineTypeEnum.Bulk }
                , { label: 'PID', value: QuarantineTypeEnum.PID }
            ];
        }
        
        if (this.isBphp()) {
            return [
                { label: 'Bulk', value: QuarantineTypeEnum.Bulk }
                , { label: 'PID', value: QuarantineTypeEnum.PID }
                , { label: 'Loose Pills', value: QuarantineTypeEnum.LoosePills }
            ];
        }
        
        return [];
    });

    quarantineDisabledSignal = signal<boolean>(false);
    quarantineDisabledComputed = computed(() => 
        this.quarantineDisabledSignal() 
        || this.loosePillService.isAddingLoosePill()
        || this.routingWorkflow() === SysInvWorkflowEnum.Quarantine
    );

    quarantineMode = signal<QuarantineTypeEnum | null>(null);
    isLoadingContainer = signal<boolean>(false);
    isNewContainer = signal<boolean>(true);
    // #endregion

    // #region Issuance Config
    issuanceOptions = computed<ShortcutOption[]>(() => {
        if (
            !this.isBphp() 
            && this.needsChdInventoryChoice()
        ) {
            return [
                { label: 'Bulk', value: InventoryTypeEnum.Bulk }
                , { label: 'PID', value: InventoryTypeEnum.Issuance }
            ];
        }

        return []
    });

    issuanceDisabled = linkedSignal<boolean>(() => {
        if (this.routingWorkflow() === SysInvWorkflowEnum.Issuance) { return true; }
        return false;
    });
    // #endregion

    // #region FTSU Config
    ftsuOptions = computed<ShortcutOption[]>(() => {
        let ftsuInvs = this.inventories().filter(i => i.ftsuStatus != FTSUStatusEnum.Completed);
        let options: ShortcutOption[] = [];

        if (ftsuInvs.some(i => i.type === InventoryTypeEnum.Bulk)) {
            options.push({ label: 'Bulk', value: InventoryTypeEnum.Bulk });
        }

        if (ftsuInvs.some(i => i.type === InventoryTypeEnum.Issuance)) {
            options.push({ label: 'Issuance', value: InventoryTypeEnum.Issuance });
        }

        return options.length == 1 ? [] : options;
    });

    ftsuDisabled = linkedSignal<boolean>(() => {
        if (this.routingWorkflow() === SysInvWorkflowEnum.FirstTimeSetup) { return true; }
        return false;
    });
    // #endregion

    // #region Lifecycle/Actions
    getInventories() {
        const httpParams = new HttpParams().set('facilityId', this.infoService.userFacilityId() ?? 0);

        this.http.get<SystemMessage>('api/Dashboard/GetShortcutInventories', { params: httpParams })
            .subscribe((sm: SystemMessage) => {
                if (sm.isSuccessful) {
                    const inventories = sm.model as SysInvInventoryVm[];
                    this.inventories.set(inventories);
                } else { this.toastr.showError(sm.message ?? ''); }
            }
        );
    }
    
    readonly bindToFacilityChanges = effect(() => {
        this.infoService.facilityChangeNotifier();
        if (!this.infoService.isInitialized()) { return; }
        untracked(() => { this.getInventories(); });
    });

    clickShortcut(
        shortcut: ShortcutItem
        , op: Popover
        , event: Event
    ) {
        if (
            shortcut.options?.length 
            || shortcut.additionalTemplate
        ) {
            const isSameShortcut = this.popoverShortcut()?.title === shortcut.title;

            // Clicking the same shortcut again should just toggle it closed.
            if (
                isSameShortcut 
                && op.overlayVisible
            ) {
                op.hide();
                return;
            }

            // Switching to a different shortcut
            this.popoverShortcut.set(shortcut);
            this.activeAddtionalTemplate.set(false);

            if (op.overlayVisible) {
                // Need to specify anchor because of weird bug happening when popover was already opened.
                // When we switched to different options, the target element on the click event
                // was referencing the button's inner content instead of the button itself.
                const anchor = (event.currentTarget as HTMLElement) ?? (event.target as HTMLElement).closest('button');
                op.hide();
                setTimeout(() => {
                    this.popoverShortcut.set(shortcut);
                    this.activeAddtionalTemplate.set(false);
                    op.show(event, anchor);
                }, 0);
            } else { op.show(event); }

            if (shortcut.options?.length !== 0) { return; }
        }
        
        void shortcut.action();
    }

    // Popover content can change size after it's shown 
    // so keep it repositioned within the viewport for as long as it's open.
    onShortcutShow() {
        const popover = this.op();
        const container = popover?.container;
        if (
            !popover 
            || !container
        ) { return; }

        this.popoverResizeObserver?.disconnect();
        this.popoverResizeObserver = new ResizeObserver(() => popover.align());
        this.popoverResizeObserver.observe(container);
    }

    onShortcutClose() {
        this.popoverResizeObserver?.disconnect();
        this.popoverResizeObserver = undefined;

        // General
        this.popoverShortcut.set(null);
        this.activeAddtionalTemplate.set(false);
        this.selectedInventory.set(null);

        // Transfer
        this.selectedInventoryTransferType.set(null);
        this.destinationTransferInventoryId.set(null);
        this.isOutOfCountyTransfer.set(false);
        this.outOfCountyTransferComment.set('');

        // Quarantine
        this.quarantineMode.set(null);
        this.loosePillService.container.set(null!);
    }

    triggerAction(
        shortcut: ShortcutItem | null
        , value?: any
        , op?: Popover
    ) {
        if (!shortcut) { return; }

        void shortcut.action(value);

        if (
            !this.activeAddtionalTemplate() 
            && op?.overlayVisible
        ) { op?.hide(); }
    }

    private getInventory(
        type?: InventoryTypeEnum
        , invs: 'all' | 'ftsu' | 'available' = 'available'
    ): SysInvInventoryVm | null {
        let inventories = [];
        switch(invs) {
            case('all'): { inventories = this.inventories(); break; }
            case('ftsu'): { inventories = this.inventories().filter(i => i.ftsuStatus !== FTSUStatusEnum.Completed); break; }
            case('available'): { inventories = this.availableInventories(); break; }
        }

        if (inventories.length === 0) { return null; }

        // BPHP case: always use the Both inventory.
        const bothInventory = inventories.find(i => i.type === InventoryTypeEnum.Both);

        if (bothInventory) { return bothInventory; }

        // Normal CHD single-inventory case.
        if (inventories.length === 1) { return inventories[0]; }

        // Normal CHD bulk/issuance choice case.
        if (type != null) { return inventories.find(i => i.type === type) ?? null; }

        return null;
    }
    // #endregion
    
    // #region Inventory
    private openInventory(type?: InventoryChoiceEnum) {
        const invType = 
            this.isBphp() 
                ? null 
                : type ==  InventoryChoiceEnum.Bulk 
                    ? InventoryTypeEnum.Bulk 
                    : InventoryTypeEnum.Issuance;

        const inventory = this.getInventory(invType!);

        if (!inventory) { return; }

        this.inventoryDisabled.set(true);

        // type is InventoryTypeEnum.Bulk or InventoryTypeEnum.Issuance.
        if (this.isBphp()) {
            void this.router.navigate(
                ['/inv/invs/products', inventory.sysInvInventoryId]
                , { queryParams: { tab: type } }
            );
            return;
        }

        void this.router.navigate(['/inv/invs/products', inventory.sysInvInventoryId]);
    }
    // #endregion

    // #region Order
    private tryCreateOrder(type?: InventoryTypeEnum) {
        const inventory = this.getInventory(type);
        if (!inventory) { return; }
        
        if (this.orderDisabled()) { return; }

        this.orderDisabled.set(true);
        // Not while this inventory's quarantined items need a quarantine action (DEPCS-4950). Checked on the
        // click, since the shortcut can stand for more than one inventory. The server refuses the same way.
        this.http.get<OrderingBlockVm>(`api/SysInv/GetOrderingBlock?inventoryId=${inventory.sysInvInventoryId}`)
            .subscribe({
                next: block => {
                    if (block?.isBlocked) {
                        this.toastr.showWarning(block.message!);
                        this.orderDisabled.set(false);
                        return;
                    }
                    this.startOrder(inventory, type);
                }
                // The check itself failed: carry on, and let the server decide.
                , error: () => this.startOrder(inventory, type),
            });
    }

    private startOrder(
        inventory: SysInvInventoryVm
        , type?: InventoryTypeEnum
    ) {
        const params: any = { inventoryId: inventory.sysInvInventoryId };
        if (type !== undefined) { params.type = type; }
        this.http.get<SystemMessage>("api/Dashboard/CheckExistingOrder", { params: params })
            .subscribe((sm: SystemMessage) => {
                if (sm.isSuccessful) {
                    this.http.get<SystemMessage>(`api/SysInv/GetInitialOrder?inventoryId=${inventory.sysInvInventoryId}`)
                        .pipe(
                            tap((resp: SystemMessage) => {
                                if (resp.isSuccessful) {
                                    this.orderService.order.set(resp.model);
                                    const order = this.orderService.order();
                                    if (order) { order.orderType = type; }
                                    if (
                                        !this.orderService.requirementsMissing() 
                                        && !this.orderService.isMissingProgramAccounts()
                                    ) { this.orderService.createOrder(); } 
                                    else {
                                        let modal = this.orderService.openShippingModal();
                                        modal.componentInstance.isCreating = true;
                                        modal.result.then().catch(() => { this.orderDisabled.set(false); });
                                    }
                                } else { this.toastr.showError('Unable to create order: ' + resp.message); }
                            })
                        ).subscribe();
                } else {
                    // Existing order found, navigate to the existing order list.
                    const orderId = sm.value;
                    // open modal to nav user
                    const modal = this.modalService.open(ConfirmationModalComponent);
                    modal.componentInstance.modalRef = modal;
                    modal.componentInstance.confirmationMessage = "There is already an in progress order for this inventory."
                    modal.componentInstance.confirmWord = "View Order";
                    // For non-users, use the standard promise-based approach
                    modal.result.then((emittedValue) => {
                        if (emittedValue) {
                            this.routingWorkflow.set(SysInvWorkflowEnum.Order);
                            void this.router.navigate([`/inv/invs/orders/${orderId}/${inventory.sysInvInventoryId}`]);
                        }
                            
                        this.orderDisabled.set(false);
                    }).catch(() => { this.orderDisabled.set(false); });
                }
            }
        );
    }
    // #endregion

    // #region Receiving
    private openReceivingList(type?: InventoryTypeEnum) {
        const inventory = this.getInventory(type);

        if (!inventory) { return; }

        this.receivingDisabled.set(true);
        this.routingWorkflow.set(SysInvWorkflowEnum.Receiving);
        void this.router.navigate([`/inv/invs/receiving/${inventory.sysInvInventoryId}`]);
    }
    // #endregion

    // #region Transfer
    private openTransferOptions(value?: InventoryTypeEnum) {
        const inventory = this.getInventory(value);

        if (!inventory) { return; }

        if (!inventory.canTransfer) {
            const missingItems: string[] = [];

            if (!inventory.hasValidMedicalDirector) { missingItems.push("valid Medical Director License"); }

            if (!inventory.has54Account) {
                missingItems.push("valid Restricted Rx Drug Distributor-Government Program Permit (DBPR-54)");
            }

            const missingTransferRequirementsMsg = `You do not have a ${missingItems.join(" and a ")}.`;

            const modal = this.modalService.open(ConfirmationModalComponent);
            modal.componentInstance.modalRef = modal;
            modal.componentInstance.confirmationMessage = `${missingTransferRequirementsMsg} ` 
                + `Please submit an Account Update Request by clicking the Accounts button.`;
            modal.componentInstance.confirmWord = "Go to Accounts";

            modal.result.then((emittedValue) => {
                if (emittedValue) { this.goToAccounts(inventory.organizationId); }
                this.transferDisabled.set(false);
            }).catch(() => this.transferDisabled.set(false));

            return;
        }

        this.activeAddtionalTemplate.set(true);
        this.selectedInventory.set(inventory);
        this.selectedInventoryTransferType.set(value ?? inventory.type);

        this.transferItemsLoading.set(true);
        this.transferService.getInventoriesForTransfer(inventory.sysInvInventoryId, this.selectedInventoryTransferType()!)
            .pipe(finalize(() => this.transferItemsLoading.set(false)))
            .subscribe((lst: any) => { this.inventoryTransferSelectItems.set(lst); });
    }

    createTransfer() {
        if (
            this.isOutOfCountyTransfer() 
            && (
                this.outOfCountyTransferComment() == null
                || this.outOfCountyTransferComment() == ''
            )
        ) {
            this.toastr.showError('Must set a comment for an out-of-county transfer.', { timeOut: 5000 });
            return;
        }

        if (this.transferDisabled()) { return; }
        
        this.transferDisabled.set(true);

        this.transferService.createTransfer(
            this.selectedInventory()!.sysInvInventoryId
            , this.destinationTransferInventoryId()!
            , this.selectedInventoryTransferType()!
            , this.isOutOfCountyTransfer()
            , this.outOfCountyTransferComment()
        ).subscribe({
            next: (sm:SystemMessage) => {
                if (sm.isSuccessful) {
                    this.routingWorkflow.set(SysInvWorkflowEnum.Transfer);
                    void this.router.navigateByUrl(
                        `/inv/invs/transfers/edit/${sm?.value}/${this.selectedInventory()!.sysInvInventoryId}`
                    );
                } else {
                    this.toastr.showError(sm.message ?? '');
                    this.transferDisabled.set(false);
                }
            }
            , error: (err) => {
                this.toastr.showError("Something went wrong creating your transfer. Please try again.");
                console.error(err.error?.message)
                this.transferDisabled.set(false);
            }
        });
    }

    selectTransferItem(item: SelectItem) { this.isOutOfCountyTransfer.set(!!item?.additionalData?.showTransferWarning); }

    getTransferInvItemTooltip(item: SelectItem) {
        if (item?.additionalData?.showTransferWarning) { return 'Out of County Transfer'; }
        return '';
    }
    
    getCreateTransferBtnTooltip() {
        if (this.destinationTransferInventoryId() == null) { return 'Please select an Inventory'; }
        if (this.isOutOfCountyTransfer()) {
            return (
                'Out of County Transfers require additional approval. A support ticket will be created upon submission. ' 
                + 'This must be approved before the transfer can be started.'
            );
        }
        return '';
    }

    goToAccounts(orgId: number) {
        window.open(
            `/admin/organizations/orgs/edit/${orgId}?tab=Organization%20Accounts`
            , '_blank'
        );
    }
    // #endregion

    // #region Reconciliation
    private openReconciliationSelect(value?: ReconTypeEnum) {
        const invType = this.isBphp() 
            ? null 
            : value ==  ReconTypeEnum.Bulk 
                ? InventoryTypeEnum.Bulk 
                : InventoryTypeEnum.Issuance;

        const inventory = this.getInventory(invType!);
        
        if (!inventory) { return; }
        if (this.reconDisabled()) { return; }

        this.reconDisabled.set(true);

        const recon = {
            sysInvInventoryId: inventory.sysInvInventoryId
            , reconciliationType: value
            , statusEnum: ReconStatusEnum.CountOne
        };

        this.http.post<SystemMessage>('api/SysInv/Reconciliation/CreateReconciliation', recon)
            .pipe(finalize(() => this.reconDisabled.set(false)))
            .subscribe({
                next: (sm: SystemMessage) => {
                    if (sm.isSuccessful) {
                        const model = sm.model as ReconciliationVm;
                        this.routingWorkflow.set(SysInvWorkflowEnum.Reconciliation);
                        void this.router.navigateByUrl(
                            `/inv/invs/reconciliations/${model.sysInvReconciliationId}/${model.sysInvInventoryId}`
                        );
                    }
                }
            }
        );    
    }
    // #endregion
    
    // #region Quarantine
    openQuarantineSelect(value?: QuarantineTypeEnum) {
        const invType = this.isBphp() 
            ? null 
            : value ==  QuarantineTypeEnum.Bulk 
                ? InventoryTypeEnum.Bulk 
                : InventoryTypeEnum.Issuance;

        const inventory = this.getInventory(invType!);
        
        if (!inventory) { return; }

        this.selectedInventory.set(inventory);
        this.activeAddtionalTemplate.set(true);

        const mode: QuarantineTypeEnum = inventory.type == InventoryTypeEnum.Bulk 
            ? QuarantineTypeEnum.Bulk 
            : QuarantineTypeEnum.PID;

        this.quarantineMode.set(value ?? mode);

        if (value == QuarantineTypeEnum.LoosePills) { this.getOpenContainer(inventory.sysInvInventoryId); }      
    }

    /**
     * Manual quarantine needs a planned disposition for every item (DEPCS-4947). Scans go into the
     * shared intake modal, and the quarantine page opens once something has been added.
     */
    private readonly intakeService = inject(QuarantineIntakeService);
    private readonly intakeClosed = this.intakeService.closed$
        .pipe(takeUntilDestroyed())
        .subscribe(o => {
            if (o.added === 0) { return; }
            this.routingWorkflow.set(SysInvWorkflowEnum.Quarantine);
            void this.router.navigateByUrl(o.mode === 'pid'
                ? `/inv/invs/quarantine/pid/${o.inventoryId}`
                : `/inv/invs/quarantine/packages/${o.inventoryId}`);
        });

    processPidScan(scanStr: string) {
        const { pid, label } = parsePidScan(scanStr);
        this.intakeService.open('pid', this.selectedInventory()!.sysInvInventoryId, { pid, pidLabel: label });
    }

    openAddBulkModal() {
        this.intakeService.open('bulk', this.selectedInventory()!.sysInvInventoryId, { manualEntry: true });
    }

    openPidManualEntry() {
        this.intakeService.open('pid', this.selectedInventory()!.sysInvInventoryId, { manualEntry: true });
    }

    processBulkScan(barcode: ProductBarcode) {
        if (!this.hasValidBarcode(barcode)) {
            this.toastr.showMessage(
                'The barcode was not valid. Make sure there are no blemishes on the barcode.',
                'error'
            );
            return;
        }

        const inventoryId = this.selectedInventory()!.sysInvInventoryId;
        // A multipack parent is split first, on its own, before the intake modal opens.
        this.intakeService.scanBulk(
            inventoryId
            , {
                model: {
                    businessArea: BusinessAreaEnum.SysInv
                    , inventoryId
                    , ndc: barcode.ndc10
                    , lotNumber: barcode.lotNumber
                    , expDate: barcode.expirationDate
                    , serialNumber: barcode.serialNumber
                    , gtin: barcode.gtin?.toString()
                    , childIndex: barcode.childIndex
                }
            }
        );
    }

    private hasValidBarcode(barcode: ProductBarcode) {
        return barcode.ndc10 != null && barcode.lotNumber != null && barcode.expirationDate != null;
    }

    getOpenContainer(inventoryId: number) {
        this.isLoadingContainer.set(true);
        const params = new HttpParams().set('inventoryId', inventoryId);
        this.http.post<SystemMessage>(`api/Quarantines/LoosePills/GetOpenContainer`, null, { params })
            .subscribe({
                next: (response) => {
                    if (response.isSuccessful) {
                        this.isNewContainer.set(!response.model);

                        const model: LoosePillContainerVm = (response.model as LoosePillContainerVm) 
                            ?? {
                                loosePillContainerId: 0
                                , sysInvInventoryId: inventoryId
                                , currentWeight: 0
                                , dateCreated: new Date()
                                , createdByUserId: 0
                            };

                        this.loosePillService.container.set(model);
                    }
                    this.isLoadingContainer.set(false);
                }
                , error: (err) => { this.isLoadingContainer.set(false); }
            }
        );
    }

    loosePillAdded() {
        this.quarantineDisabledSignal.set(true);
        this.routingWorkflow.set(SysInvWorkflowEnum.Quarantine);
        void this.router.navigateByUrl(`/inv/invs/quarantine/loose-pills/${this.selectedInventory()!.sysInvInventoryId}`);
    }
    // #endregion

    // #region Issuance
    private tryCreateIssuance(value?: InventoryTypeEnum) {
        const inventory = this.getInventory(value);
        if (!inventory) { return; }

        this.issuanceDisabled.set(true);

        this.issuanceService.createIssuance(inventory.sysInvInventoryId, value == InventoryTypeEnum.Bulk)
            .pipe(finalize(() => this.issuanceDisabled.set(false)))
            .subscribe((sm: SystemMessage) => {
                if (sm.isSuccessful) {
                    this.toastr.showSuccess(`Issuance created`);
                    this.routingWorkflow.set(SysInvWorkflowEnum.Issuance);
                    void this.router.navigateByUrl(`/inv/invs/issuances/edit/${sm.value}/${inventory.sysInvInventoryId}`);
                } else { this.toastr.showError(sm?.message ?? ''); }
            }
        );
    }
    // #endregion

    // #region FTSU
    openFtsu(value?: InventoryTypeEnum) {
        const inventory = this.getInventory(value, 'ftsu');

        if (!inventory) { return; }

        this.ftsuDisabled.set(true);
        this.routingWorkflow.set(SysInvWorkflowEnum.FirstTimeSetup);
        void this.router.navigate(['/inv/invs/products', inventory.sysInvInventoryId]);
    }
    // #endregion

    // #region Btn Styling
    readonly BULK_COLOR = ColorService.getColor('--bulk');
    readonly BULK_COLOR_HOVER = ColorService.adjustColor(this.BULK_COLOR, {lightness: 7});
    readonly BULK_DT  = this.makeBtnDt(this.BULK_COLOR, this.BULK_COLOR_HOVER!);
    readonly ISSUANCE_COLOR = ColorService.getColor('--issuance');
    readonly ISSUANCE_COLOR_HOVER = ColorService.adjustColor(this.ISSUANCE_COLOR, {lightness: 16});
    readonly ISSUANCE_DT = this.makeBtnDt(this.ISSUANCE_COLOR, this.ISSUANCE_COLOR_HOVER!);
    readonly BOTH_COLOR = ColorService.getColor('--both');
    readonly BOTH_COLOR_HOVER = ColorService.adjustColor(this.BOTH_COLOR, {lightness: 7});
    readonly BOTH_DT = this.makeBtnDt(this.BOTH_COLOR, this.BOTH_COLOR_HOVER!);

    makeBtnDt(
        mainColor: string
        , hoverColor: string
    ) {
        return {
            root: {
                primaryBackground: mainColor
                , primaryBorderColor: mainColor
                , primaryHoverBackground: hoverColor
                , primaryHoverBorderColor: mainColor
                , primaryActiveBackground: mainColor
                , primaryActiveBorderColor: mainColor
            }
        };
    }

    getBtnDt(
        workflow: SysInvWorkflowEnum
        , value: InventoryChoiceEnum | QuarantineTypeEnum | ReconTypeEnum | InventoryTypeEnum
    ) {
        switch(workflow) {
            case (SysInvWorkflowEnum.Transfer):
            case (SysInvWorkflowEnum.Issuance):
            case (SysInvWorkflowEnum.Receiving):
            case (SysInvWorkflowEnum.FirstTimeSetup):
            case (SysInvWorkflowEnum.Order): {
                switch(value) {
                    case (InventoryTypeEnum.Bulk): return this.BULK_DT;
                    case (InventoryTypeEnum.Issuance): return this.ISSUANCE_DT;
                }
                break;
            } case (SysInvWorkflowEnum.Quarantine): {
                switch(value) {
                    case (QuarantineTypeEnum.Bulk): return this.BULK_DT;
                    case (QuarantineTypeEnum.PID): return this.ISSUANCE_DT;
                    case (QuarantineTypeEnum.LoosePills): return this.BOTH_DT;
                }
                break;
            } case (SysInvWorkflowEnum.Reconciliation): {
                switch(value) {
                    case (ReconTypeEnum.Bulk): return this.BULK_DT;
                    case (ReconTypeEnum.PID): return this.ISSUANCE_DT;
                    case (ReconTypeEnum.Staging): return this.BOTH_DT;
                }
                break;
            } default: {
                switch(value) {
                    case (InventoryChoiceEnum.Bulk): return this.BULK_DT;
                    case (InventoryChoiceEnum.PID): return this.ISSUANCE_DT;
                    case (InventoryChoiceEnum.Staging): return this.BOTH_DT;
                }
            }
        }
    }
    // #endregion
}

interface ShortcutItem {
    title: string;
    icon: IconProp;
    workflow?: SysInvWorkflowEnum;
    action: (value?: any) => void | Promise<void>;
    options?: ShortcutOption[];
    additionalTemplate?: string;
    disabled: boolean;
}

interface ShortcutOption {
    label: string;
    value?: any;
}

enum QuarantineTypeEnum {
    Bulk = 0
    , PID = 1
    , LoosePills = 2
}